<script>
import { h, resolveComponent } from 'vue';

export default {
  name: 'lego-tree-node',
  props: {
    node: { type: Object, required: true },
    position: { type: Array, required: true },
    draggable: { type: Boolean, default: true }
  },
  inject: [
    'checkbox',
    'labelInfo',
    'labelAction',
    'inlineEdit',
    'rules', // props or data
    'reRender',
    'onClickNode',
    'onClickExpand',
    'onClickCheckbox',
    'emitClickDeleteButton',
    'emitClickEditButton',
    'dragstart',
    'dragleave',
    'dragenter',
    'dragend'
  ], // methods
  render(props) {
    let node = props.node;
    let treeInfo = props.node.treeInfo;
    let childNodes = props.node.children;
    let dragFlag = props.draggable;
    let onClickNode = (e) => props.onClickNode(e, node, props.position);
    let onClickExpand = (e) => props.onClickExpand(e, node);
    let onClickCheckbox = (e) => props.onClickCheckbox(e, node, props.position);
    let emitClickDeleteButton = (e) => props.emitClickDeleteButton(e, node, props.position);
    let emitClickEditButton = (e) => props.emitClickEditButton(e, node, props.position);
    let dragstart = (e) => props.dragstart(e, props.position);
    let dragleaveOnLabel = (e) => {
      clearTimeout(e.target._expandOnTimeout);
      return props.dragleave(e);
    };
    let dragenterOnLabel = (e) => {
      e.target._expandOnTimeout = setTimeout(() => onClickExpand(e), 1000);
      props.dragenter(e, node);
    };
    let dragleave = (e) => props.dragleave(e);
    let dragenter = (e) => props.dragenter(e);
    let dragendOnHeader = (e) => props.dragend(e, props.position, 'BEFORE');
    let dragendOnLabel = (e) => props.dragend(e, props.position, 'CHILD');
    let dragendOnFooter = (e) => props.dragend(e, props.position, 'AFTER');

    let toggleIcon = '';
    const legoIcon = resolveComponent('lego-icon');
    const legoIconWrapper = resolveComponent('lego-icon-wrapper');
    const legoTextField = resolveComponent('lego-text-field');
    const legoTreeNode = resolveComponent('lego-tree-node');
    if (treeInfo.hasChild) {
      toggleIcon = h(legoIcon, {
        icon: treeInfo.expanded ? 'remove' : 'add',
        spacing: true,
        iconSize: 16,
        onClick: onClickExpand
      });
    }

    let toggleButton = h(
      legoIconWrapper,
      {
        class: 'lego-tree-node__contents__toggle-button'
      },
      () => [toggleIcon]
    );

    let checkbox = '';
    let $_checkIcon = treeInfo.checked;
    if (typeof $_checkIcon !== 'string') {
      $_checkIcon = $_checkIcon ? 'checked' : 'box';
    }
    if (props.checkbox) {
      checkbox = h(
        'div',
        {
          class: {
            'lego-tree-node__contents__checkbox': true,
            'lego-tree-node__contents__checkbox--checked': treeInfo.checked
          }
        },
        [
          h(legoIcon, {
            icon: $_checkIcon,
            iconSize: 16,
            iconType: 'picto',
            onClick: onClickCheckbox
          })
        ]
      );
    }

    let label;
    if (treeInfo.inlineEdit) {
      let validateAndUpdate = () => {
        // label.componentInstance not exist. but will be hoisted after mount complete
        if (label.componentInstance.validate() === true) {
          treeInfo.inlineEdit = false;
          props.reRender();
        }
      };
      label = h(legoTextField, {
        small: true,
        value: treeInfo.name,
        rules: props.rules,
        onClick: (e) => {
          e.stopPropagation();
        },
        onInput: (newVal) => {
          treeInfo.name = newVal;
        },
        onBlur: validateAndUpdate,
        onEnter: validateAndUpdate
      });
    } else {
      label = props.$slots.treeNodeLabel && props.$slots.treeNodeLabel({ node: props.node });
      if (!label) {
        if (typeof props.labelInfo === 'function') {
          label = props.labelInfo(props.node);
        } else if (typeof props.labelInfo === 'object' && props.labelInfo.field) {
          label = [];
          for (let field of props.labelInfo.field) {
            let curr = props.node;
            for (let keyword of field.split('.')) {
              curr = curr[keyword];
            }
            label.push(curr);
          }
          label = label.join(props.labelInfo.separator || '');
        } else {
          label = '';
        }
      }
    }
    let labelActionGroup = '';
    if (props.labelAction) {
      labelActionGroup = h(
        'div',
        {
          class: 'lego-tree-node__contents__action-group'
        },
        [
          h(
            legoIconWrapper,
            {
              class: 'lego-tree-node__contents__action-icon'
            },
            [
              h(legoIcon, {
                icon: 'modify',
                iconSize: '14px',
                onClick: props.inlineEdit
                  ? (e) => {
                      e.stopPropagation();
                      treeInfo.inlineEdit = true;
                      props.reRender();
                    }
                  : emitClickEditButton
              })
            ]
          ),
          h(
            legoIconWrapper,
            {
              class: 'lego-tree-node__contents__action-icon'
            },
            [
              h(legoIcon, {
                icon: 'delete',
                iconSize: '14px',
                onClick: emitClickDeleteButton
              })
            ]
          )
        ]
      );
    }
    let contentsLabel = h(
      'div',
      {
        class: {
          'lego-tree-node__contents__label': true,
          'lego-tree-node__contents__label--inline-edit': treeInfo.inlineEdit,
          'lego-tree-node__contents__label--with-action-group': props.labelAction
        },
        draggable: dragFlag,
        style: [`${treeInfo.inlineEdit} ? "line-height:0;overflow:visible;" : "`],
        onClick: onClickNode,
        onDragstart: dragstart,
        onDragenter: dragenterOnLabel,
        onDragleave: dragleaveOnLabel,
        onDragover: (e) => e.preventDefault(),
        onDrop: dragendOnLabel
      },
      [label, labelActionGroup]
    );

    let content = h(
      'div',
      {
        class: {
          'lego-tree-node__contents': true,
          'lego-tree-node__contents--searched': treeInfo.searched,
          'lego-tree-node__contents--selected': treeInfo.selected,
          'lego-tree-node__contents--root': treeInfo.depth === 0
        }
      },
      [toggleButton, checkbox, contentsLabel]
    );

    let childrenWrapper = '';
    if (treeInfo.hasChild && treeInfo.expanded) {
      let childTreeNodes = [];
      let idx = 0;
      if (childNodes && childNodes.length > 0) {
        for (let child of childNodes) {
          let childPosition = props.position.slice(0);
          childPosition.push(idx);
          childTreeNodes.push(
            h(legoTreeNode, {
              node: child,
              position: childPosition,
              draggable: dragFlag
            })
          );
          idx += 1;
        }
      } else {
        childTreeNodes.push(
          h('div', {}, [
            'loading...' // FIXME: 임시로 넣어놓음. 추후 수정필요
          ])
        );
      }
      childrenWrapper = h(
        'div',
        {
          class: 'lego-tree-node__vertical-line'
        },
        childTreeNodes
      );
    }

    let contentHeader = '';
    if (props.position[props.position.length - 1] === 0) {
      contentHeader = h('div', {
        class: 'lego-tree-node__header',
        onDragenter: dragenter,
        onDragleave: dragleave,
        onDragover: (e) => e.preventDefault(),
        onDrop: dragendOnHeader
      });
    }

    let contentFooter = h('div', {
      class: 'lego-tree-node__footer',
      onDragenter: dragenter,
      onDragleave: dragleave,
      onDragover: (e) => e.preventDefault(),
      onDrop: dragendOnFooter
    });

    return h(
      'div',
      {
        class: 'lego-tree-node'
      },
      [contentHeader, content, childrenWrapper, contentFooter]
    );
    /*
     * <div class="lego-tree-node">
     *     <content-header />
     *     <content />
     *     <childrenWrapper />
     *     <contentFooter />
     * </div>
     * */
  }
};
</script>
